import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Avatar, Button, Layout, Menu, Tooltip } from "antd";
import { AuthContext } from "../../context/AuthContext";
import data from "./navbarPOC.json";
import {
  BarChartOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  TruckOutlined,
  BookOutlined,
  SettingOutlined,
  CreditCardOutlined,
  HistoryOutlined,
  QuestionCircleOutlined,
  ContactsOutlined,
  LogoutOutlined,
  RightOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import defaultLogo from "../../img/black-logo.png";
import blackLogo from "../../img/blackLogo.png";
import blackLogoAlone from "../../img/blackLogoAlone.png";
import whiteLogo from "../../img/whiteLogo.png";
import whiteLogoAlone from "../../img/whiteLogoAlone.png";
import "./sideBar.css";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";

import packageJson from "../../../package.json";
import {
  canAccessBilling,
  canAccessDrivers,
  canAccessLibrary,
  canAccessSetup,
  canAccessTransports,
  canAccessWelcome,
  canUseFleetUI,
} from "../../auth/fleetRoles";

const { Sider } = Layout;

export function isColorDark(hexColor) {
  const color = hexColor.replace("#", "");
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

const iconMap = {
  BarChartOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  TruckOutlined,
  BookOutlined,
  SettingOutlined,
  CreditCardOutlined,
  HistoryOutlined,
  QuestionCircleOutlined,
  ContactsOutlined,
};

const CustomIcon = ({ name }) => {
  if (name === "ParkingSign") {
    return <span className="ant-menu-item-icon sidebar-parking-icon">P</span>;
  }
  const IconComponent = iconMap[name];
  return IconComponent ? <IconComponent /> : null;
};

function SideBar({ isExpanded, onToggle, fleetLogo: fleetLogoProp, lockCollapsed = false }) {
  const { getFleetID, logOutUser, fleetData, fleetRole } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loggedUser, setLoggedUser] = useState(null);
  const [fleetLogo, setFleetLogo] = useState(defaultLogo);
  const [commitVersion, setCommitVersion] = useState(process.env.REACT_APP_GIT_COMMIT || "local");
  const [runtimeVersion, setRuntimeVersion] = useState(
    process.env.REACT_APP_RUNTIME_VERSION || process.env.REACT_APP_BUILD_VERSION || packageJson.version
  );

  const primaryColor = "var(--chakra-colors-gandolapp-500)";

  const darkBackground = isColorDark(primaryColor);
  const textColor = darkBackground ? "white" : "black";

  const getFleetDetails = async () => {
    const fleetId = getFleetID();
    if (!fleetId) return;

    try {
      const docRef = doc(db, "fleets", fleetId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setLoggedUser(data?.name || null);
        setFleetLogo(data?.logo || defaultLogo);
      } else {
        setFleetLogo(defaultLogo);
      }
    } catch (error) {
      console.error("Error al obtener detalles del fleet", error);
      setFleetLogo(defaultLogo);
    }
  };

  useEffect(() => {
    setCommitVersion(process.env.REACT_APP_GIT_COMMIT || "local");
    setRuntimeVersion(
      process.env.REACT_APP_RUNTIME_VERSION || process.env.REACT_APP_BUILD_VERSION || packageJson.version
    );
    if (fleetLogoProp) {
      setFleetLogo(fleetLogoProp);
    } else if (fleetData) {
      setLoggedUser(fleetData?.name || null);
      setFleetLogo(fleetData?.logo || defaultLogo);
    } else {
      getFleetDetails();
    }

    const handleLogoUpdated = (e) => {
      setFleetLogo(e.detail || defaultLogo);
    };

    window.addEventListener("logoUpdated", handleLogoUpdated);

    return () => {
      window.removeEventListener("logoUpdated", handleLogoUpdated);
    };
  }, [getFleetID, fleetData]);

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(" ");
    if (words.length === 1) return words[0][0].toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    await logOutUser();
    navigate("/login");
  };

  const normalizeLink = (link) => (link?.startsWith("/") ? link : `/${link}`);

  const footerLogo = darkBackground ? whiteLogo : blackLogo;
  const footerLogoAlone = darkBackground ? whiteLogoAlone : blackLogoAlone;

  const buildMenuItems = (items) =>
    items.map((item) => {
      const link = normalizeLink(item.link);
      return {
        key: link,
        icon: <CustomIcon name={item.emoticon} />,
        label: t(item.nameKey),
      };
    });

  const isLinkAllowedByRole = (key) => {
    if (!fleetRole) return false;
    if (key === "/welcome" || key === "/map") return canAccessWelcome(fleetRole);
    if (key === "/drivers") return canAccessDrivers(fleetRole);
    if (key === "/transports" || key === "/orders-history" || key === "/directory") return canAccessTransports(fleetRole);
    if (key === "/library" || key === "/parking") return canAccessLibrary(fleetRole);
    if (key === "/setup") return canAccessSetup(fleetRole);
    return false;
  };

  const workspaceSourceItems = buildMenuItems(data.menu).filter((item) => isLinkAllowedByRole(item.key));
  const workspaceByKey = new Map(workspaceSourceItems.map((item) => [item.key, item]));
  if (canAccessBilling(fleetRole)) {
    workspaceByKey.set("/billing", {
      key: "/billing",
      icon: <CreditCardOutlined />,
      label: t("navbar.billing", { defaultValue: "Billing" }),
    });
  }
  const groupedWorkspaceSections = [
    ["/welcome"],
    ["/map", "/drivers", "/transports", "/orders-history", "/directory"],
    ["/library", "/parking"],
    ["/setup", "/billing"],
  ];
  const workspaceItems = [];
  groupedWorkspaceSections.forEach((sectionKeys) => {
    const sectionItems = sectionKeys
      .map((key) => workspaceByKey.get(key))
      .filter(Boolean);
    if (sectionItems.length === 0) return;
    if (workspaceItems.length > 0) {
      workspaceItems.push({
        type: "divider",
        key: `divider-${workspaceItems.length}`,
      });
    }
    workspaceItems.push(...sectionItems);
  });

  const allMenuKeys = [...workspaceItems]
    .filter((item) => item.type !== "divider")
    .map((item) => item.key);
  const selectedKey =
    allMenuKeys.find((key) => location.pathname === key || location.pathname.startsWith(`${key}/`)) ||
    null;
  const accountSelected = location.pathname === "/account" || location.pathname.startsWith("/account/");

  const collapsed = lockCollapsed ? true : !isExpanded;
  const handleAccountManagement = () => {
    if (canUseFleetUI(fleetRole)) {
      navigate("/account");
    }
  };

  return (
    <Sider
      className="sidebar sidebar-antd"
      width={208}
      collapsedWidth={64}
      collapsed={collapsed}
      trigger={null}
      style={{ backgroundColor: primaryColor, color: textColor }}
      data-collapsed={collapsed ? "true" : "false"}
    >
      <div className="sidebar-shell">
        <div className="sidebar-top">
          <div className="sidebar-header">
            <div className="sidebar-brand">
              <img src={fleetLogo} alt="logo" className="sidebar-logo" />
            </div>
            {!collapsed && !lockCollapsed && (
              <Tooltip title={t("sidebar.collapse", { defaultValue: "Collapse" })}>
                <Button
                  type="default"
                  shape="circle"
                  className="sidebar-trigger-btn"
                  icon={<LeftOutlined />}
                  onClick={onToggle}
                  style={{ color: textColor }}
                />
              </Tooltip>
            )}
          </div>

          <Menu
            className="sidebar-menu sidebar-menu-main"
            mode="inline"
            inlineCollapsed={collapsed}
            items={workspaceItems}
            selectedKeys={selectedKey ? [selectedKey] : []}
            onClick={({ key }) => navigate(key)}
            style={{ background: "transparent", color: textColor }}
          />

        </div>

        <div className="sidebar-footer">
          {loggedUser && canUseFleetUI(fleetRole) && (
            <Button
              type="text"
              className={`sidebar-account-btn${accountSelected ? " sidebar-account-btn-active" : ""}`}
              onClick={handleAccountManagement}
              style={{ color: textColor }}
              title={t("navbar.account", { defaultValue: "Account" })}
            >
              <div className="sidebar-user">
                <Avatar
                  className="sidebar-user-avatar"
                  style={{
                    color: textColor,
                    backgroundColor: darkBackground ? "#ffffff33" : "#00000022",
                  }}
                >
                  {getInitials(loggedUser)}
                </Avatar>
                {!collapsed && (
                  <span className="sidebar-username" style={{ color: textColor }} title={loggedUser}>
                    {loggedUser}
                  </span>
                )}
              </div>
            </Button>
          )}

          <Button
            type="text"
            className="sidebar-logout-btn"
            icon={<LogoutOutlined style={{ color: textColor }} />}
            onClick={handleLogout}
            style={{ color: textColor }}
          >
            {!collapsed ? t("navbar.logout") : null}
          </Button>

          <div className="version-item">
            {!collapsed && (
              <span style={{ color: textColor }}>
                {t("sidebar.connectedBy", { defaultValue: "Connected by" })}
              </span>
            )}
            <a href="https://gandolapp.com/" target="_blank" rel="noreferrer">
              <img
                src={collapsed ? footerLogoAlone : footerLogo}
                alt="logo"
                className="version-logo"
              />
            </a>
            {!collapsed && (
              <span style={{ color: textColor }} className="sidebar-version">
                {`v${runtimeVersion}.${/^[a-f0-9]{6,40}$/i.test(commitVersion) ? commitVersion.slice(0, 8) : commitVersion}`}
              </span>
            )}
          </div>
        </div>
      </div>
      {collapsed && !lockCollapsed && (
        <div className="sidebar-edge-trigger-wrap">
          <Tooltip title={t("sidebar.expand", { defaultValue: "Expand" })}>
            <Button
              type="default"
              shape="circle"
              className="sidebar-trigger-btn sidebar-trigger-btn-edge"
              icon={<RightOutlined />}
              onClick={onToggle}
              style={{ color: textColor }}
            />
          </Tooltip>
        </div>
      )}
    </Sider>
  );
}

export default SideBar;
